// Styles
import styled from 'styled-components/macro'

// Core
import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useHistory, useLocation} from 'react-router-dom'

// Components, services, etc
import {getCompsByPid} from 'actions/compsActions'
import {openModal} from 'actions/modalActions'
import {createOrder} from 'actions/ordersActions'
import Button from 'components/Button'
import CompCard from 'components/CompCard'
import FlatList from 'components/FlatList'
import Modal from 'components/Modal'
import {LABELS_DATA} from 'constants/modalLabels'
import {LOG_IN_SIGN_UP_MODAL} from 'constants/modalsConstants'
import {CLEARPROP} from 'constants/productCardConstants'
import {ERROR, LOADED, LOADING} from 'constants/reduxStatuses'
import {
  compsListSelector,
  compsStatusLoadingSelector,
  compsStatusSelector,
  compsSubjectPidSelector,
  isUserAuthenticatedSelector,
  propertyDataSelector
} from 'selectors'
import {createSuppliedAddressObject} from 'services/addressServices'
import * as isBrowser from 'services/browserServices'

// 3rd-party
import CircularProgress from '@material-ui/core/CircularProgress'
import classnames from 'classnames'
import {ALL_ORDERS_PATH} from 'constants/appRoutesConstants'
import {getMixpanelPage} from 'services/mixpanel'
import {getProductNavPath} from 'services/productNavigationServices'
import type {CreateOrderRequestBody, CreateOrderResponse} from 'types/orderTypes'

type Props = {
  open: boolean
  handleClose: () => void
  handleExited: () => void
  className?: string
  pid: string
}

const CompsModal = ({open, pid, handleClose, handleExited, className}: Props) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const subjectScrollContainerRef = useRef<HTMLDivElement>(null)
  const compsScrollContainerRef = useRef<HTMLDivElement>(null)
  const scrollValuesRef = useRef({
    scrollHeight: 0
  })
  const isLoggedIn = useSelector(isUserAuthenticatedSelector)
  const compsData = useSelector(compsListSelector)
  const subjectData = useSelector(propertyDataSelector)
  const isCompsLoading = useSelector(compsStatusLoadingSelector)
  const subjectPid = useSelector(compsSubjectPidSelector)
  const compsStatus = useSelector(compsStatusSelector)
  const [submitting, setSubmitting] = useState(false)

  const handleScrollSync: React.UIEventHandler<HTMLElement> = e => {
    const {current: compsContainer} = compsScrollContainerRef
    const {current: subjectContainer} = subjectScrollContainerRef
    const {scrollHeight} = scrollValuesRef.current
    const scrollTop = (e.target as HTMLElement).scrollTop
    if (scrollHeight !== scrollTop) {
      compsContainer!.scrollTop = scrollTop
      subjectContainer!.scrollTop = scrollTop
      scrollValuesRef.current.scrollHeight = scrollTop
    }
  }

  useEffect(() => {
    const isPidDifferent = subjectPid && subjectPid !== pid
    const isError = compsStatus === ERROR
    const isLoaded = compsStatus === LOADED
    const isLoading = compsStatus === LOADING
    if ((!isLoaded || isPidDifferent) && !isError) {
      if (!isLoading) {
        dispatch(getCompsByPid(pid))
      }
    }
  }, [compsStatus, dispatch, pid, subjectPid])

  const onClose = () => {
    handleClose()
  }

  const handleOrderAbandoned = () => setSubmitting(false)

  const getClearPropClick = () => {
    const suppliedAddress = createSuppliedAddressObject(subjectData)
    const order: CreateOrderRequestBody = {
      pid,
      productType: CLEARPROP,
      suppliedAddress
    }
    const dispatchOrder = () => {
      return dispatch(
        createOrder({
          orderPayload: order,
          handleOrder,
          handleOrderError,
          handleOrderAbandoned,
          mixpanelEventData: {
            page: getMixpanelPage(location.pathname),
            component: 'comps modal'
          }
        })
      )
    }

    if (isLoggedIn) {
      setSubmitting(true)
      dispatchOrder()
    } else {
      dispatch(
        openModal({
          modalType: LOG_IN_SIGN_UP_MODAL,
          mode: 'signUp',
          onSuccess: dispatchOrder,
          mixpanelEventData: {
            initiatedFrom: 'Comps Modal'
          }
        })
      )
    }
  }

  const handleOrder = (order: CreateOrderResponse) => {
    setSubmitting(false)
    onClose()

    if (order.productOrderId) {
      history.push(getProductNavPath(order))
    } else {
      history.push(ALL_ORDERS_PATH)
    }
  }

  const handleOrderError = () => {
    setSubmitting(false)
    history.push(ALL_ORDERS_PATH)
  }

  return (
    <CompsModal.Styled
      className={classnames('comps-modal', className)}
      open={open}
      disableEnforceFocus
      showCloseButton
      size='lg'
      onClose={onClose}
      onExited={handleExited}
    >
      <Modal.Header>
        <CompsModal.HeaderContent getClearPropClick={getClearPropClick} submitting={submitting} />
      </Modal.Header>
      <Modal.Content>
        <div
          ref={subjectScrollContainerRef}
          onScroll={handleScrollSync}
          className={classnames('subject-scroll-container', {
            'add-scrollbar-padding': isBrowser.isEdge || isBrowser.isIE
          })}
        >
          <FlatList className='labels' items={LABELS_DATA} />
          <CompCard labels={LABELS_DATA} propertyData={subjectData} isSubject />
        </div>
        <div
          ref={compsScrollContainerRef}
          onScroll={handleScrollSync}
          className='comps-scroll-container'
        >
          {isCompsLoading ? (
            <div className='loading-wrap'>
              {/* inner div needed for IE11 */}
              <div>
                <CircularProgress size={100} />
              </div>
            </div>
          ) : (
            compsData.map(compData => (
              <CompCard key={compData.pid} labels={LABELS_DATA} propertyData={compData} />
            ))
          )}
        </div>
      </Modal.Content>
    </CompsModal.Styled>
  )
}

type HeaderProps = {
  getClearPropClick: () => any
  submitting: boolean
}

CompsModal.HeaderContent = ({getClearPropClick, submitting}: HeaderProps) => (
  <div className='header-content'>
    <div className='title'>Similar Homes</div>
    <div className='view-more'>
      <p>
        Want to view More?&nbsp;
        <Button
          title='Get ClearProp'
          variant='tertiary'
          onClick={getClearPropClick}
          disabled={submitting}
        />
      </p>
    </div>
  </div>
)

CompsModal.Styled = styled(Modal)`
  .portal-MuiDialog-paperScrollPaper {
    height: calc(100% - 64px);
    /* Header 72 px, max content 740px, footer 24px */
    max-height: 836px;
  }

  .header-content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex: 2;
    margin-right: 8px;

    .title {
      font-weight: 500;
      font-size: 20px;
      line-height: 28px;
      color: ${({theme}) => theme.colors.grayscale.black};
    }
    .view-more {
      p {
        display: flex;
        align-items: center;
        margin: 0;
      }
    }
  }

  .content {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    max-height: calc(100% - 92px);
    overflow: hidden;
    position: relative;

    .subject-scroll-container {
      position: absolute;
      top: 0;
      left: 24px;
      overflow-y: auto;
      height: 100%;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: flex-start;
      -ms-overflow-style: none; /* Hide scrollbar on IE and Edge */
      scrollbar-width: none; /* Hide Scrollbar on Firefox */

      /* Hide Scrollbar on Chrome */
      &::-webkit-scrollbar {
        display: none;
      }
      .labels {
        min-width: 128px;
        width: 128px;
        /* address 88px */
        margin-top: 88px;
        font-size: 14px;
        line-height: 22px;
        color: ${({theme}) => theme.colors.grayscale.gray};

        li {
          height: 48px;
        }
      }
    }

    .subject-scroll-container.add-scrollbar-padding > .comp-card.subject {
      padding-bottom: 17px;
    }

    .comps-scroll-container {
      position: absolute;
      top: 0;
      left: 420px;
      overflow: auto;
      height: 100%;
      /* 396px for subject-scroll-container and 24px of padding on each side */
      width: calc(100% - 444px);
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: flex-start;
      overflow: auto;
      .loading-wrap {
        height: 100%;
        display: flex;
        flex-grow: 1;
        align-items: center;
        justify-content: center;

        /* inner div needed for IE11 */
        > div {
          display: flex;
          width: 48px;
        }
      }

      .comp-card:not(:first-child) {
        margin-left: -1px;
      }
      .comp-card:first-child {
        border-left: none;
      }
    }
  }
  .footer {
    min-height: 24px;
    padding: 0;
    border-top: none;
  }
`

export default CompsModal
